import { ReactNode, useEffect, useMemo, useState } from 'react';
import arrayDiff from 'arr-diff';
import { cloneDeep, isEqual, isFunction, omit, uniq } from 'lodash';
import { format as formatDate } from 'date-fns';
import {
    DodWizardContext,
    DodWizardContextValue,
} from '@/components/DodConfigEditor/DodRunConfigWizard/DodWizardContext';
import { RowColConfig } from '@/components/DodConfigEditor/types';
import { defaultProductOrder, factSetToDisplayNames, MARKET_CHARACTERISTICS_HIERARCHY_ORDER_IDS, hasShareOfBrandFacts } from '@/components/DodConfigEditor/common/utils';
import { toAxisDef } from '@/components/DodConfigEditor/toAxisDef';
import { helpIconContent } from '@/components/DodConfigEditor/common/helpIconContent';
import { useApp } from '@/contexts/UserContext';
import { TimePeriodRange } from '@/utils/timePeriod/TimePeriodRange';
import { caseInsensitiveReverseSort, caseInsensitiveSort } from '@/utils/Sort';
import {
    DodColDef,
    DodFilter,
    DodFilters,
    DodProductSubDimension,
    DodRowDef,
    DodRunConfig,
    SortType,
} from '@/types/DodRun';
import { uniqueId } from 'lodash';
import { v4 as uuid } from 'uuid';

export interface DodWizardProviderProps {
    children: ReactNode | ReactNode[];
}

function hasLayoutStepSelections(rowColConfigs: RowColConfig[]): boolean {
    // Only layout interactions that currently trigger the estimate-data-points API should promote
    // the config to step 5. At the moment that means product-level Hide / Stack toggles. We
    // intentionally exclude Page By because it does not trigger the same API path.
    return rowColConfigs.some((config) => config.type === 'products' && (config.hide || config.stack));
}

export function DodWizardProvider({children}: DodWizardProviderProps) {
    const { maxDataDates } = useApp();
    const almostUniqueId = Date.now().toString(36).toUpperCase();
    const sessionId = uniqueId(`dod-wizard-session-${almostUniqueId}-${uuid()}`);

    const maxDate = useMemo<string>(() => {
        return formatDate(maxDataDates.rms!, 'yyyyMMdd');
    }, [maxDataDates]);

    const [value, setValue] = useState<DodWizardContextValue>({
        sessionId,
        dataPointCount: 0,
        setDataPointCount(dataPointCount: number): void {
            setValue(prevValue => ({
                ...prevValue,
                dataPointCount
            }))
        },
        calculatingDataPoints: false,
        setCalculatingDataPoints(calculatingDataPoints: boolean): void {
            setValue(prevValue => ({
                ...prevValue,
                calculatingDataPoints
            }))
        },
        runType: 'subscription',
        runConfig: cloneDeep(DEFAULT_RUN_CONFIG),
        applyValues(values: ((prevState: DodWizardContextValue) => DodWizardContextValue) | Partial<DodWizardContextValue>): void {
            setValue(prevValue => {
                if (isFunction(values)) {
                    return values(prevValue);
                }

                return {
                    ...prevValue,
                    ...values,
                }
            })
        },
        setRunConfig(value: ((prevState: DodRunConfig) => DodRunConfig) | DodRunConfig): void {
            setValue(prevState => ({
                ...prevState,
                runConfig: isFunction(value) ? value(prevState.runConfig) : value
            }));
        },
        rowColConfigs: cloneDeep(DEFAULT_ROW_COL_CONFIGS),
        updateRowColConfigs(options) {
            const shouldRequireExistingLayout = options?.shouldRequireExistingLayout === true;
            const shouldSetWizardStepToLayout = options?.shouldSetWizardStepToLayout !== false;

            setValue(value => {
                const {rowColConfigs, runConfig} = value;
                const {filters, layout} = runConfig;

                const hasBuiltLayout = Boolean(layout.rows?.length || layout.columns?.length);
                const shouldWaitForLayoutBuild = shouldRequireExistingLayout && !hasBuiltLayout;

                // BYZ-19581: skip until Layout has been built once; an early populated layout would break the server's empty-layout path.
                if (shouldWaitForLayoutBuild) {
                    return value;
                }

                const configs = rowColConfigs;
                const currentFilters: DodFilters = filters;
                const isQuickLayoutApplied: boolean = runConfig.layout.quickLayoutCode !== 'cl' && runConfig.layout.quickLayoutCode !== undefined;

                // get the filters that are already in the rowColConfigs (we don't want to move these because they may have
                // already been ordered, we'll use these later to decide what needs to added and removed
                const previousProductDims: string[] = configs.map(v => v.dim).filter(dim => !excludedDims.includes(dim));

                // get the new product filters, excluding characteristics and custom characteristic (because we'll
                // handle them next) and markets and time periods (because they can't be changed in this step)
                let currentProductDims: (keyof DodFilters | string)[] = Object.entries(currentFilters)
                    .filter(([key, value]) => {
                        const filter = value as DodFilter;
                        return !allCharKeys.includes(key as any) && !excludedDims.includes(key) &&
                            (filter?.values === 'all' || (filter?.values?.length || 0) > 0 || (filter?.summedSelections?.length || 0) > 0)
                    })
                    .map(([key]) => key)
                    .sort((a, b) => {
                        return defaultProductOrder.indexOf(a as DodProductSubDimension) - defaultProductOrder.indexOf(b as DodProductSubDimension)
                    });

                // ensure we always have product descriptions if we have upcs
                if (currentProductDims.includes('upcs') && !currentProductDims.includes('productDescriptions')) {
                    const upcsIndex = currentProductDims.indexOf('upcs');
                    currentProductDims.splice(upcsIndex + 1, 0, 'productDescriptions');
                }

                // In order to alphabetically order values in chars and custom chars while retaining
                // group selections of char and cust char, performing separate sorts and merging as below
                const characteristics = Object.values({
                    ...currentFilters.characteristics
                }).map(value => value.displayName).sort(caseInsensitiveSort);

                const customCharacteristics = Object.values({
                    ...currentFilters.customCharacteristics
                }).map(value => value.displayName).sort(caseInsensitiveSort);

                const ppgs = Object.values({
                    ...currentFilters.ppgs
                }).map(value => value.displayName).sort(caseInsensitiveSort);

                // find any characteristic filters that are in use
                const currentCharacteristicsAndPpgs = [...characteristics, ...customCharacteristics, ...ppgs]

                // add the characteristics and custom characteristics to the list
                currentProductDims.push(...currentCharacteristicsAndPpgs);

                // figure out what needs to be added and removed.
                const addedProductDimensions: string[] = arrayDiff(currentProductDims, previousProductDims);
                const removedProductDimensions: string[] = arrayDiff(previousProductDims, currentProductDims);

                // Products share one grouped axis: prefer Category, else a surviving real product (skips the '' placeholder and removed dims).
                const isKeptProductConfig = (config: RowColConfig) =>
                    config.type === 'products' && Boolean(config.dim) && !removedProductDimensions.includes(config.dim);
                const axisSourceConfig = configs.find(config => config.dim === 'categories' && isKeptProductConfig(config))
                    ?? configs.find(isKeptProductConfig);
                const axis = axisSourceConfig?.axis ?? 'row';

                // figure where to insert any new values. this could be skipped if there are no new values, but that's
                // over-optimized and unnecessary.  If there are no products this will produce -1 which inserts at the end
                // @ts-ignore
                const lastProductIndex: number = configs.findLastIndex(v => v.type === 'products') + 1;
                // replace the new values to the existing configs
                configs.splice(lastProductIndex, 0, ...addedProductDimensions.map<RowColConfig>(dim => ({
                    axis,
                    type: 'products',
                    dim,
                    sortType: 'manual',
                    values: [],
                    hide: false,
                    pageBy: false,
                    stack: false,
                })))

                // return a new array by filtering out any removed values.  Even if no values are removed this produces
                // a new array so react can be "react" accordingly
                let updatedConfigs = configs.filter(config => config.type !== 'products' || !removedProductDimensions.includes(config.dim));

                if (removedProductDimensions.includes('brands') && hasShareOfBrandFacts(runConfig.facts)) {
                    updatedConfigs = updatedConfigs.map((config) =>
                        config.dim === 'upcs' && config.hide
                            ? { ...config, hide: false }
                            : config
                    );
                }

                // find previous and current market names
                let marketConfig = updatedConfigs.find(config => config.type === 'markets');
                if (!marketConfig) {
                    marketConfig = {
                        type: 'markets',
                        dim: 'markets',
                        axis: 'col',
                        sortType: 'default',
                        hide: false,
                        values: [],
                        pageBy: false,
                        stack: false,
                    };
                    updatedConfigs.push(marketConfig);
                }

                // get sort applied for markets
                const marketSort: SortType | 'default' = marketConfig.sortType;
                const previousMarketNames: string[] = marketConfig.values;
                // get the names of the markets and summed market selections
                const markets = filters.markets || { values: [], summedSelections: [] };
                const currentMarketNames: string[] = [
                    ...(markets.values || []).map(market => market.name),
                    ...(markets.summedSelections || []).map(ss => ss.name)
                ].sort(caseInsensitiveSort);
                // figure out which markets need to be added and removed.
                const addedMarketNames: string[] = arrayDiff(currentMarketNames, previousMarketNames);
                const removedMarketNames: string[] = arrayDiff(previousMarketNames, currentMarketNames);
                let sortedMarkets: string[] = [];
                const { values: marketValues = [], summedSelections: marketSummedSelections = [] } = runConfig.filters.markets || {};
                if (marketSort === 'asc' || (marketSort === 'manual' && isQuickLayoutApplied)) {
                    // `marketSort === 'asc'` => BYZ-9320 maintain asc sort for markets if 'asc' sort selected
                    // `(marketSort === 'manual' && isQuickLayoutApplied)` => BYZ-9320 to maintain default asc order everytime quicklayout is applied in manual mode (this may happen in rerun)
                    sortedMarkets = marketValues.map((market) => market.name).sort(caseInsensitiveSort);
                    marketConfig.values = [...sortedMarkets, ...marketSummedSelections.map((ss) => ss.name)];
                } else if (
                    marketSort === 'desc'
                ) {
                    // `marketSort === 'desc'` => BYZ-9320 maintain desc sort for markets if 'desc' sort selected
                    sortedMarkets = marketValues.map((market) => market.name).sort(caseInsensitiveReverseSort);
                    marketConfig.values = [...sortedMarkets, ...marketSummedSelections.map((ss) => ss.name)];
                } else {
                    // BYZ-9320 append markets to end if no sort is selected (means 'default' or 'manual' sort is applied)
                    // remove and add markets
                    marketConfig.values = [
                        ...marketConfig.values.filter((market) => !removedMarketNames.includes(market)),
                        ...addedMarketNames,
                    ];
                }

                // find previous and current market characteristics
                const previousMarketCharConfigs = updatedConfigs.filter(config => config.type === 'market_characteristics');
                const previousMarketCharDims = previousMarketCharConfigs.map(c => c.dim);
                // Create a map for quick lookup of previous configs by dim
                const previousMarketCharConfigMap = new Map(previousMarketCharConfigs.map(c => [c.dim, c]));

                // Get market characteristic IDs sorted by hierarchy order (matching how products sorts by defaultProductOrder)
                let currentMarketCharIds: string[] = Object.entries(currentFilters.marketCharacteristics || {})
                    .filter(([_, char]) => char && (char.values === 'all' || (char.values as string[])?.length > 0))
                    .map(([id]) => id)
                    .sort((a, b) => {
                        const indexA = MARKET_CHARACTERISTICS_HIERARCHY_ORDER_IDS.indexOf(a);
                        const indexB = MARKET_CHARACTERISTICS_HIERARCHY_ORDER_IDS.indexOf(b);
                        
                        if (indexA !== -1 && indexB !== -1) {
                            return indexA - indexB;
                        }
                        if (indexA !== -1) return -1;
                        if (indexB !== -1) return 1;
                        return a.localeCompare(b);
                    });

                // Map IDs to display names for comparison with existing dims
                const currentMarketCharDims = currentMarketCharIds.map(id => {
                    const char = currentFilters.marketCharacteristics?.[id];
                    return char?.displayName || id;
                });

                // If no characteristics are selected, we still want to show the dimension group in the layout (disabled)
                // We use a placeholder dim name 'marketCharacteristics' which will not be in currentMarketCharDims
                const effectiveCurrentDims = currentMarketCharDims.length > 0 ? currentMarketCharDims : ['marketCharacteristics'];

                const addedMarketCharDims = arrayDiff(effectiveCurrentDims, previousMarketCharDims);
                const removedMarketCharDims = arrayDiff(previousMarketCharDims, effectiveCurrentDims);

                // Market characteristics axis should be preserved from prior config (e.g. re-run/saved layout); if none exists, fall back to the current market axis
                const marketCharAxis = marketConfig.axis;
                const previousMarketCharAxis = previousMarketCharConfigs[0]?.axis ?? marketCharAxis;
                const marketIndex = updatedConfigs.findLastIndex(v => v.type === 'markets');

                // filter out removed ones FIRST
                updatedConfigs = updatedConfigs.filter(config => !(config.type === 'market_characteristics' && removedMarketCharDims.includes(config.dim)));

                // THEN calculate insertion point (after removal, when indices are fresh)
                // When adding market characteristics for the first time, insert right after markets
                // When they already exist (in the FILTERED array), insert after the last one to preserve manual reordering
                const remainingMarketCharIndex = updatedConfigs.findLastIndex(v => v.type === 'market_characteristics');
                const lastMarketCharIndex = remainingMarketCharIndex !== -1 
                    ? remainingMarketCharIndex + 1  // Insert after existing market characteristics (preserves manual reordering)
                    : marketIndex + 1;                // If none exist after removal, insert right after markets

                // add new ones, preserving sortType and other properties from previously loaded configs
                updatedConfigs.splice(lastMarketCharIndex, 0, ...addedMarketCharDims.map<RowColConfig>(dim => {
                    const previousConfig = previousMarketCharConfigMap.get(dim);
                    return {
                        axis: previousMarketCharAxis,
                        type: 'market_characteristics',
                        dim,
                        // Preserve sortType from loaded config if it exists, otherwise default to 'default'
                        sortType: previousConfig?.sortType ?? 'default',
                        values: previousConfig?.values ?? [],
                        hide: previousConfig?.hide ?? false,
                        pageBy: previousConfig?.pageBy ?? false,
                        stack: previousConfig?.stack ?? false,
                    };
                }));

                // Market characteristics axis sync is handled elsewhere:
                // - When first added: synced to current market axis above
                // - When quick layout applied: applyPresetLayout sets correct axes
                // - When market axis changes: setRowColConfigs detects change and converts to custom layout
                // Do NOT re-sync here as it interferes with user's independent positioning choices

                // find previous and current time periods
                let timePeriodConfig = updatedConfigs.find(config => config.type === 'time_periods');
                if (!timePeriodConfig) {
                    timePeriodConfig = {
                        type: 'time_periods',
                        dim: 'timePeriods',
                        axis: 'col',
                        sortType: 'default',
                        hide: false,
                        values: [],
                        pageBy: false,
                        stack: false,
                    };
                    updatedConfigs.push(timePeriodConfig);
                }

                // get sort applied for timeperiods
                const timePeriodSort: SortType | "default" = timePeriodConfig.sortType;
                const previousTimePeriods: string[] = timePeriodConfig.values;
                const timePeriodFilters = currentFilters.timePeriods || { values: [], summedSelections: [] };
                // we need to flatten this array to account for custom time periods which are stored as nested arrays
                const orderedTimePeriods = (timePeriodFilters.values || []).flat()
                    .map(tp => new TimePeriodRange(tp, maxDate))
                    .toSorted(TimePeriodRange.compareDesc)
                    .map(tp => tp.toLegacyDodString())
                const currentTimePeriods: string[] = [
                    ...orderedTimePeriods,
                    ... (timePeriodFilters.summedSelections || []).map(ss => ss.name)
                ];
                // figure out what needs to be added and removed.
                const addedTimePeriods: string[] = arrayDiff(currentTimePeriods, previousTimePeriods);
                const removedTimePeriods: string[] = arrayDiff(previousTimePeriods, currentTimePeriods);
                
                let sortedTimePeriods: string[] = [];
                
                const { values: tpValues = [], summedSelections: tpSummedSelections = [] } = currentFilters.timePeriods || {};

                const shouldSortTimePeriodsAscending = timePeriodSort === 'asc';
                const shouldReapplyQuickLayoutTimePeriodOrder = timePeriodSort === 'manual' && isQuickLayoutApplied && addedTimePeriods.length > 0;
                const shouldSortTimePeriodsDescending = timePeriodSort === 'desc' || timePeriodSort === 'default' || shouldReapplyQuickLayoutTimePeriodOrder;

                if (shouldSortTimePeriodsAscending) {
                    // `timePeriodSort === 'asc'` => BYZ-12159 maintain asc sort for timeperiods if 'asc' sort selected
                    sortedTimePeriods = tpValues
                        .flat()
                        .map((tp) => new TimePeriodRange(tp, maxDate))
                        .toSorted(TimePeriodRange.compareAsc)
                        .map((tp) => tp.toLegacyDodString());
                    timePeriodConfig.values = [...sortedTimePeriods, ...tpSummedSelections.map((ss) => ss.name)];
                } else if (shouldSortTimePeriodsDescending) {
                    // `timePeriodSort === 'desc'` => BYZ-12159 maintain desc sort for timeperiods if 'desc' sort selected

                    // `timePeriodSort === 'default'` => BYZ-12159 Testcase 3(b) - to maintain reverse chronological order whenever to maintain default order irrespective of new slections after navigating back from layout (or)
                    
                    // Manual quick layouts are re-sorted only when additions are detected; otherwise saved order is preserved.
                    
                    sortedTimePeriods = tpValues
                        .flat()
                        .map((tp) => new TimePeriodRange(tp, maxDate))
                        .toSorted(TimePeriodRange.compareDesc)
                        .map((tp) => tp.toLegacyDodString());
                    timePeriodConfig.values = [...sortedTimePeriods, ...tpSummedSelections.map((ss) => ss.name)];
                } else {
                    // Custom manual selections append additions; quick layouts without additions keep surviving order.
                    timePeriodConfig.values = [
                        ...timePeriodConfig.values.filter((timePeriod) => !removedTimePeriods.includes(timePeriod)),
                        ...addedTimePeriods,
                    ];
                }
                

                // find previous and current facts
                let factConfig = updatedConfigs.find(config => config.type === 'facts');
                if (!factConfig) {
                    factConfig = {
                        type: 'facts',
                        dim: 'facts',
                        axis: 'col',
                        sortType: 'default',
                        hide: false,
                        values: [],
                        pageBy: false,
                        stack: false,
                    };
                    updatedConfigs.push(factConfig);
                }

                const previousFacts: string[] = factConfig.values;
                const facts = runConfig.facts;

                const currentFacts: string[] = facts.map(factSetToDisplayNames).flat();
                // figure out what needs to be added and removed.
                const addFacts: string[] = arrayDiff(currentFacts, previousFacts);
                const removedFacts: string[] = arrayDiff(previousFacts, currentFacts);
                // remove and add fact dimensions
                factConfig.values = [
                    ...factConfig.values.filter(fact => !removedFacts.includes(fact)),
                    ...addFacts
                ];

                const nextRowColConfigs = cloneDeep(updatedConfigs);

                const rows = nextRowColConfigs.filter(v => v.axis === 'row').map(toAxisDef) as DodRowDef[];
                const columns = nextRowColConfigs.filter(v => v.axis === 'col').map(toAxisDef) as DodColDef[];

                const nextRunConfig = {
                    ...runConfig,
                    layout: {
                        ...layout,
                        rows,
                        columns,
                        // updateRowColConfigs runs after layout-side edits change the row/column model.
                        // If the resulting config contains an API-triggering layout selection, stamp
                        // step/wizardState as 5 so subsequent estimate calls from the layout tab are
                        // attributed to the Layout step.
                        // Callers outside the Layout step opt out so the step marker set by handleStepChange is kept.
                        ...(shouldSetWizardStepToLayout && hasLayoutStepSelections(nextRowColConfigs)
                            ? { extractWizardStep: 5 as const, wizardState: 5 as const }
                            : {}),
                    }
                };

                if (isEqual(nextRowColConfigs, value.rowColConfigs) && isEqual(nextRunConfig, value.runConfig)) {
                    return value;
                }

                return {
                    ...value,
                    rowColConfigs: nextRowColConfigs,
                    runConfig: nextRunConfig,
                };
            });
        },
        setPreset(key, value) {
            setValue(context => ({
                ...context,
                presets: {
                    ...context.presets,
                    [key]: value
                }
            }))
        },
        deletePreset(key) {
            setValue((context) => ({ ...context, presets: omit(context.presets, key) }));
        },
        presets: {},
        setRowColConfigs(value: ((prevState: RowColConfig[]) => RowColConfig[]) | RowColConfig[]): void {
            setValue((prevState) => {
                const rowColConfigs = isFunction(value) ? value(prevState.rowColConfigs) : value;
                // @ts-ignore
                const rows: DodRowDef[] = rowColConfigs.filter((config) => config.axis === 'row').map(toAxisDef);
                const columns: DodColDef[] = rowColConfigs.filter((config) => config.axis === 'col').map(toAxisDef);

                // setRowColConfigs is the custom-edit path (drag/drop, axis change, hide, stack, reorder).
                // Any manual edit converts a quick layout to a custom layout ('cl'), unless the user is on a
                // template or saved layout (in which case we leave quickLayoutCode untouched).
                const quickLayoutCode =
                    (!prevState.runConfig.layout.templateId && !prevState.runConfig.layout.savedLayoutId)
                        ? 'cl'
                        : undefined;

                const runConfig = {
                    ...prevState.runConfig,
                    layout: {
                        ...prevState.runConfig.layout,
                        rows,
                        columns,
                        quickLayoutCode,
                        savedLayoutId: undefined,
                        templateId: undefined,
                        // setRowColConfigs is the main path used by hide/stack interactions in the layout
                        // designer. We only stamp step 5 when those flags are present; otherwise we leave
                        // any existing step marker untouched to avoid incorrectly promoting other layout edits.
                        ...(hasLayoutStepSelections(rowColConfigs)
                            ? { extractWizardStep: 5 as const, wizardState: 5 as const }
                            : {}),
                    },
                };

                return {
                    ...prevState,
                    rowColConfigs,
                    runConfig,
                };
            });
        },
        suppressQuickLayoutWarning: false,
        setSuppressQuickLayoutWarning(suppressQuickLayoutWarning: boolean): void {
            setValue(value => ({
                ...value,
                suppressQuickLayoutWarning
            }))
        },
        setTimePeriodsReordered(timePeriodsReordered: boolean): void {
            setValue(prevState => ({
                ...prevState,
                timePeriodsReordered
            }))
        },
        timePeriodsReordered: false,
        individualRunSeriesNames: [],
        seriesNames: {
          individual: new Set<string>(),
          scheduled: new Set<string>(),
        },
        setSeriesNames(seriesNames: { [key: string]: Set<string> }): void {
          setValue((prevValue) => ({
              ...prevValue,
              seriesNames,
          }));
        },
        allExceptSelectionFields: [],
        addAllExceptSelectionField(field: string | string[]): void {
            setValue(prevState => ({
                ...prevState,
                allExceptSelectionFields: uniq([...prevState.allExceptSelectionFields, ...(Array.isArray(field) ? field : [field])])
            }))
        },
        removeAllExceptSelectionField(field: string | string[]): void {
            setValue(prevState => {
                if (!Array.isArray(field)) {
                    // Check field type and reset isExcluded flag appropriately
                    // Standard product dimensions use string keys, but PPGs use numeric keys
                    // Each type needs different handling to maintain proper key types in the state
                    const DodProductSubDimensions: DodProductSubDimension[] = ['departments', 'superCategories','categories','subcategories','upcs','productDescriptions','parentCompanies','manufacturers','brands'];
                    if (DodProductSubDimensions.includes(field as DodProductSubDimension)) {
                        return {
                            ...prevState,
                            runConfig: { ...prevState.runConfig, filters: { ...prevState.runConfig.filters, [field as keyof DodFilters]: { ...prevState.runConfig.filters[field as keyof DodFilters], isExcluded: false } } }, // reset isExcluded when removing from allExceptSelectionFields
                            allExceptSelectionFields: prevState.allExceptSelectionFields.filter(f => !Array.isArray(field) ? f !== field : !field.includes(f))
                        }
                    } else {
                        const charKeys = prevState.runConfig.filters.characteristics ? Object.keys(prevState.runConfig.filters.characteristics) : [];
                        const customCharKeys = prevState.runConfig.filters.customCharacteristics ? Object.keys(prevState.runConfig.filters.customCharacteristics) : [];
                        const marketCharKeys = prevState.runConfig.filters.marketCharacteristics ? Object.keys(prevState.runConfig.filters.marketCharacteristics) : [];
                        const ppgKeys = prevState.runConfig.filters.ppgs ? Object.keys(prevState.runConfig.filters.ppgs) : [];
                        if (charKeys.includes(field as string)) {
                            const updatedCharacteristics = { ...prevState.runConfig.filters.characteristics[field as string], isExcluded: false };
                            return {
                                ...prevState,
                                runConfig: { ...prevState.runConfig, filters: { ...prevState.runConfig.filters, characteristics: { ...prevState.runConfig.filters.characteristics, [field as string]: updatedCharacteristics } } }, // reset isExcluded when removing from allExceptSelectionFields
                                allExceptSelectionFields: prevState.allExceptSelectionFields.filter(f => !Array.isArray(field) ? f !== field : !field.includes(f))
                            }
                        } else if (customCharKeys.includes(field as string)) {
                            const updatedCustomCharacteristics = { ...prevState.runConfig.filters.customCharacteristics[field as string], isExcluded: false };
                            return {
                                ...prevState,
                                runConfig: { ...prevState.runConfig, filters: { ...prevState.runConfig.filters, customCharacteristics: { ...prevState.runConfig.filters.customCharacteristics, [field as string]: updatedCustomCharacteristics } } }, // reset isExcluded when removing from allExceptSelectionFields
                                allExceptSelectionFields: prevState.allExceptSelectionFields.filter(f => !Array.isArray(field) ? f !== field : !field.includes(f))
                            }
                        } else if (marketCharKeys.includes(field as string)) {
                            const updatedMarketCharacteristics = { ...prevState.runConfig.filters.marketCharacteristics?.[field as string], isExcluded: false };
                            return {
                                ...prevState,
                                runConfig: { ...prevState.runConfig, filters: { ...prevState.runConfig.filters, marketCharacteristics: { ...prevState.runConfig.filters.marketCharacteristics || {}, [field as string]: updatedMarketCharacteristics } } }, // reset isExcluded when removing from allExceptSelectionFields
                                allExceptSelectionFields: prevState.allExceptSelectionFields.filter(f => !Array.isArray(field) ? f !== field : !field.includes(f))
                            }
                        } else if (ppgKeys.includes(field)) {
                            // PPG keys are stored as numbers, not strings, so we access them directly
                            // This maintains type consistency for the ppgs object keys
                            const updatedPpgs = { ...prevState.runConfig.filters.ppgs[field], isExcluded: false };
                            const buffer = {
                                ...prevState,
                                runConfig: { ...prevState.runConfig, filters: { ...prevState.runConfig.filters, ppgs: { ...prevState.runConfig.filters.ppgs, [field]: updatedPpgs } } }, // reset isExcluded when removing from allExceptSelectionFields
                                allExceptSelectionFields: prevState.allExceptSelectionFields.filter(f => !Array.isArray(field) ? f !== field : !field.includes(f))
                            }
                            return buffer;
                        } else {
                            return {
                                ...prevState,
                                allExceptSelectionFields: prevState.allExceptSelectionFields.filter(f => !Array.isArray(field) ? f !== field : !field.includes(f))
                            }
                        }
                    }
                } else {
                    return {
                        ...prevState,
                        allExceptSelectionFields: prevState.allExceptSelectionFields.filter(f => !Array.isArray(field) ? f !== field : !field.includes(f))
                    }
                }
            });
        },
        isAllExceptSelectionField(field: string): boolean {
            return value.allExceptSelectionFields.includes(field);
        },
        isColumnsExceedingLimit: false,
        setIsColumnsExceedingLimit(exceeded: boolean): void {
            setValue(prevValue => ({
                ...prevValue,
                isColumnsExceedingLimit: exceeded
            }))
        },
        helpIconInfo: {
            products_help_icon: helpIconContent.products_help_icon,
            markets_help_icon: helpIconContent.markets_help_icon,
            time_periods_help_icon: helpIconContent.time_periods_help_icon,
            fact_help_icon: helpIconContent.fact_help_icon,
            layout_help_icon: helpIconContent.layout_help_icon,
            schedule_help_icon: helpIconContent.schedule_help_icon,
        },
        allSelectionState: {},
        setAllSelectionState(type: string): void {
            setValue(prevState => ({
                ...prevState,
                allSelectionState: {
                    ...prevState.allSelectionState,
                    [type]: !prevState.allSelectionState[type],
                }
            }))
        },
    });

    return <DodWizardContext.Provider value={value}>
        {children}
    </DodWizardContext.Provider>
}

export const DEFAULT_RUN_CONFIG: DodRunConfig = {
    filters: {
        departments: {
            values: [],
            summedSelections: [],
        },
        superCategories: {
            values: [],
            summedSelections: [],
        },
        categories: {
            values: [],
            summedSelections: [],
        },
        subcategories: {
            values: [],
            summedSelections: [],
        },
        upcs: {
            values: [],
            summedSelections: [],
        },
        productDescriptions: {
            values: [],
            summedSelections: [],
        },
        parentCompanies: {
            values: [],
            summedSelections: [],
        },
        manufacturers: {
            values: [],
            summedSelections: [],
        },
        brands: {
            values: [],
            summedSelections: [],
        },
        characteristics: {},
        customCharacteristics: {},
        ppgs: {},
        markets: {
            values: [],
            summedSelections: [],
        },
        timePeriods: {
            values: [],
            summedSelections: [],
        },
    },
    facts: [],
    layout: {
        rows: [],
        columns: [],
        quickLayoutCode: 'ql1',
        savedLayoutId: undefined,
        includeCategoryTotals: false,
        includeExtractDate: false,
        includeSubTotals: false,
        includeNoHistoryLabels: false,
        includeGranularityLevel: false,
        filterNotApplicable: false,
        overallUiOrder: ['products', 'markets', 'market_characteristics', 'time_periods', 'facts']
    },
    conditions: {
        facts: [],
        market: 'each_market',
        timePeriod: 'each_time_period',
    },
}
export const DEFAULT_ROW_COL_CONFIGS: RowColConfig[] = [
    {
        type: 'products',
        dim: '',
        axis: 'row',
        sortType: 'default',
        hide: false,
        values: [],
        pageBy: false,
        stack: false
    },
    {
        type: 'markets',
        dim: 'markets',
        axis: 'col',
        sortType: 'default',
        hide: false,
        values: [],
        pageBy: false,
        stack: false
    },
    {
        type: 'market_characteristics',
        dim: 'marketCharacteristics',
        axis: 'col',
        sortType: 'default',
        hide: false,
        values: [],
        pageBy: false,
        stack: false
    },
    {
        type: 'time_periods',
        dim: 'timePeriods',
        axis: 'col',
        sortType: 'default',
        hide: false,
        values: [],
        pageBy: false,
        stack: false
    },
    {
        type: 'facts',
        dim: 'facts',
        axis: 'col',
        sortType: 'default',
        hide: false,
        values: [],
        pageBy: false,
        stack: false
    },
];
export const excludedDims: (keyof DodFilters | string)[] = ['markets', 'timePeriods', 'facts', 'marketCharacteristics'];
export const allCharKeys: (keyof DodFilters)[] = ['characteristics', 'customCharacteristics', 'marketCharacteristics', 'ppgs'];
